/**
 * Slot machine ("Maszyny") rules. No DOM, no Node — the web reels and the API
 * have to score a 5×3 grid the same way.
 *
 * Five reels, three rows, five paylines (rows + V + inverted V). Pays 3/4/5 of a
 * symbol anywhere on a line (same as legacy `maszyny/game.php`, not left-to-right
 * consecutive). Payouts are quoted at a 10-chip bet and scale linearly. ⭐ scatter
 * is not a payline symbol; 3/4/5 award free spins. Chips are the shared casino
 * wallet (blackjack / ruletka / poker / konie / shop).
 *
 * Legacy used equal symbol odds and handed out a bonus on ~12% of spins — well over
 * 200% RTP, and free-spin chains did not die. This file keeps the layout, the
 * anywhere-on-line pay rule, and the scatter idea, but weights the reels and makes
 * the bonus rare.
 */

type Rng = () => number;

export const MASZYNY = {
  REELS: 5,
  ROWS: 3,
  STARTING_BALANCE: 1000,
  MIN_BET: 1,
  MAX_BET: 100,
  DEFAULT_BET: 10,
  /** Payout table is quoted at this stake. Chips themselves live on the casino wallet. */
  BET_UNIT: 10,
} as const;

export const MASZYNY_REGULAR_SYMBOLS = [
  "🍒",
  "🍋",
  "🍇",
  "🍉",
  "🍊",
  "💎",
  "🔔",
  "7️⃣",
] as const;

export const MASZYNY_SCATTER = "⭐";

export type MaszynyRegularSymbol = (typeof MASZYNY_REGULAR_SYMBOLS)[number];
export type MaszynySymbol = MaszynyRegularSymbol | typeof MASZYNY_SCATTER;

/** Relative strip weights. Fruit is common; diamonds are not. */
export const MASZYNY_SYMBOL_WEIGHTS: Record<MaszynyRegularSymbol, number> = {
  "🍊": 18,
  "🍋": 17,
  "🍇": 16,
  "🍉": 15,
  "🍒": 12,
  "🔔": 9,
  "7️⃣": 6,
  "💎": 4,
};

export const MASZYNY_PAYOUTS: Record<MaszynyRegularSymbol, { 3: number; 4: number; 5: number }> = {
  "💎": { 3: 30, 4: 120, 5: 500 },
  "7️⃣": { 3: 24, 4: 90, 5: 280 },
  "🔔": { 3: 18, 4: 60, 5: 150 },
  "🍒": { 3: 12, 4: 45, 5: 120 },
  "🍋": { 3: 7, 4: 25, 5: 80 },
  "🍇": { 3: 7, 4: 25, 5: 80 },
  "🍉": { 3: 7, 4: 25, 5: 80 },
  "🍊": { 3: 7, 4: 25, 5: 80 },
};

export const MASZYNY_FREE_SPINS: { 3: number; 4: number; 5: number } = {
  3: 8,
  4: 12,
  5: 15,
};

/** 1..SIDES roll. Inclusive upper bounds for 5, 4, 3 scatters, then a 1–2 tease. */
export const MASZYNY_SCATTER_ROLL = {
  SIDES: 10_000,
  FIVE: 5,
  FOUR: 25,
  THREE: 105,
  TEASE: 705,
} as const;

const REEL_BAG: MaszynyRegularSymbol[] = MASZYNY_REGULAR_SYMBOLS.flatMap((symbol) =>
  Array.from({ length: MASZYNY_SYMBOL_WEIGHTS[symbol] }, () => symbol),
);

export type MaszynyPaylineId = "top" | "middle" | "bottom" | "v" | "inv";

export interface MaszynyPayline {
  id: MaszynyPaylineId;
  label: string;
  rows: readonly [number, number, number, number, number];
}

export const MASZYNY_PAYLINES: readonly MaszynyPayline[] = [
  { id: "top", label: "Górny rząd", rows: [0, 0, 0, 0, 0] },
  { id: "middle", label: "Środkowy rząd", rows: [1, 1, 1, 1, 1] },
  { id: "bottom", label: "Dolny rząd", rows: [2, 2, 2, 2, 2] },
  { id: "v", label: "Kształt V", rows: [0, 1, 2, 1, 0] },
  { id: "inv", label: "Odwrócone V", rows: [2, 1, 0, 1, 2] },
];

export interface MaszynyCell {
  reel: number;
  row: number;
}

export interface MaszynyWinningLine {
  id: MaszynyPaylineId;
  label: string;
  symbol: MaszynyRegularSymbol;
  count: number;
  positions: MaszynyCell[];
  payout: number;
}

export interface MaszynySpinResult {
  grid: MaszynySymbol[][];
  winnings: number;
  winningPositions: MaszynyCell[];
  winningLines: MaszynyWinningLine[];
  scatterPositions: MaszynyCell[];
  scatterCount: number;
  freeSpinsAwarded: number;
}

export interface MaszynySessionResponse {
  username: string;
  balance: number;
  freeSpins: number;
  /** Stake locked for the current free-spin batch; null when playing with chips. */
  freeSpinBet: number | null;
}

export interface MaszynySpinResponse extends MaszynySpinResult {
  balance: number;
  freeSpinsRemaining: number;
  wasFreeSpin: boolean;
  freeSpinBet: number | null;
  /** Stake actually used for this spin (locked during free spins). */
  bet: number;
}

export function isMaszynyRegularSymbol(value: string): value is MaszynyRegularSymbol {
  return (MASZYNY_REGULAR_SYMBOLS as readonly string[]).includes(value);
}

export function isValidMaszynyBet(bet: number): boolean {
  return Number.isInteger(bet) && bet >= MASZYNY.MIN_BET && bet <= MASZYNY.MAX_BET;
}

export function maszynyWinAmount(baseWin: number, bet: number): number {
  return Math.round((baseWin * bet) / MASZYNY.BET_UNIT);
}

export function maszynyPayoutFor(symbol: MaszynyRegularSymbol, count: number): number | undefined {
  if (count !== 3 && count !== 4 && count !== 5) return undefined;
  return MASZYNY_PAYOUTS[symbol][count];
}

export function maszynyFreeSpinsFor(scatterCount: number): number {
  if (scatterCount !== 3 && scatterCount !== 4 && scatterCount !== 5) return 0;
  return MASZYNY_FREE_SPINS[scatterCount];
}

export function maszynyCellKey(cell: MaszynyCell): string {
  return `${cell.reel}-${cell.row}`;
}

function randInt(rng: Rng, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

function pickRegular(rng: Rng): MaszynyRegularSymbol {
  const symbol = REEL_BAG[randInt(rng, 0, REEL_BAG.length - 1)];
  return symbol ?? "🍒";
}

function shuffleInPlace<T>(items: T[], rng: Rng): T[] {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = randInt(rng, 0, i);
    const left = items[i];
    const right = items[j];
    if (left === undefined || right === undefined) continue;
    items[i] = right;
    items[j] = left;
  }
  return items;
}

/**
 * Scatter overlay. About 1.05% of grids get a 3+ bonus; 6% show a 1–2 tease.
 */
export function chooseMaszynyScatterCount(rng: () => number): number {
  const chance = randInt(rng, 1, MASZYNY_SCATTER_ROLL.SIDES);
  if (chance <= MASZYNY_SCATTER_ROLL.FIVE) return 5;
  if (chance <= MASZYNY_SCATTER_ROLL.FOUR) return 4;
  if (chance <= MASZYNY_SCATTER_ROLL.THREE) return 3;
  if (chance <= MASZYNY_SCATTER_ROLL.TEASE) return randInt(rng, 1, 2);
  return 0;
}

export function randomMaszynySymbol(rng: () => number = Math.random): MaszynyRegularSymbol {
  return pickRegular(rng);
}

export function randomMaszynyGrid(rng: () => number = Math.random): MaszynySymbol[][] {
  const grid: MaszynySymbol[][] = [];
  for (let reel = 0; reel < MASZYNY.REELS; reel += 1) {
    const column: MaszynySymbol[] = [];
    for (let row = 0; row < MASZYNY.ROWS; row += 1) {
      column.push(pickRegular(rng));
    }
    grid.push(column);
  }
  return grid;
}

export function generateMaszynyGrid(rng: () => number): MaszynySymbol[][] {
  const grid = randomMaszynyGrid(rng);
  const target = chooseMaszynyScatterCount(rng);
  const available: MaszynyCell[] = [];
  for (let reel = 0; reel < MASZYNY.REELS; reel += 1) {
    for (let row = 0; row < MASZYNY.ROWS; row += 1) {
      available.push({ reel, row });
    }
  }
  shuffleInPlace(available, rng);
  for (let i = 0; i < target; i += 1) {
    const pos = available[i];
    const column = pos ? grid[pos.reel] : undefined;
    if (!pos || !column) break;
    column[pos.row] = MASZYNY_SCATTER;
  }
  return grid;
}

function scatterCells(grid: MaszynySymbol[][]): MaszynyCell[] {
  const cells: MaszynyCell[] = [];
  for (let reel = 0; reel < MASZYNY.REELS; reel += 1) {
    for (let row = 0; row < MASZYNY.ROWS; row += 1) {
      if (grid[reel]?.[row] === MASZYNY_SCATTER) {
        cells.push({ reel, row });
      }
    }
  }
  return cells;
}

/**
 * Match like legacy PHP: count each regular symbol on the payline; 3/4/5 of the
 * same symbol pays even when the hits are not consecutive or left-aligned.
 * A 5-cell line can only hit one paying symbol at a time.
 */
function anywhereOnLineMatch(
  grid: MaszynySymbol[][],
  payline: MaszynyPayline,
): { symbol: MaszynyRegularSymbol; count: number; positions: MaszynyCell[] } | null {
  const counts = new Map<MaszynyRegularSymbol, MaszynyCell[]>();

  for (let reel = 0; reel < MASZYNY.REELS; reel += 1) {
    const row = payline.rows[reel];
    if (row === undefined) continue;
    const symbol = grid[reel]?.[row];
    if (symbol === undefined || !isMaszynyRegularSymbol(symbol)) continue;
    const positions = counts.get(symbol) ?? [];
    positions.push({ reel, row });
    counts.set(symbol, positions);
  }

  let best: { symbol: MaszynyRegularSymbol; count: number; positions: MaszynyCell[] } | null = null;
  for (const [symbol, positions] of counts) {
    const count = positions.length;
    if (maszynyPayoutFor(symbol, count) === undefined) continue;
    if (!best || count > best.count) {
      best = { symbol, count, positions };
    }
  }
  return best;
}

export function evaluateMaszynyPaylines(
  grid: MaszynySymbol[][],
  bet: number,
): {
  winnings: number;
  winningPositions: MaszynyCell[];
  winningLines: MaszynyWinningLine[];
} {
  let winnings = 0;
  const winningPositions: MaszynyCell[] = [];
  const winningLines: MaszynyWinningLine[] = [];

  for (const payline of MASZYNY_PAYLINES) {
    const match = anywhereOnLineMatch(grid, payline);
    if (!match) continue;
    const base = maszynyPayoutFor(match.symbol, match.count);
    if (base === undefined) continue;
    const payout = maszynyWinAmount(base, bet);
    winnings += payout;
    winningPositions.push(...match.positions);
    winningLines.push({
      id: payline.id,
      label: payline.label,
      symbol: match.symbol,
      count: match.count,
      positions: match.positions,
      payout,
    });
  }

  return { winnings, winningPositions, winningLines };
}

export function evaluateMaszynySpin(grid: MaszynySymbol[][], bet: number): MaszynySpinResult {
  const scatterPositions = scatterCells(grid);
  const lines = evaluateMaszynyPaylines(grid, bet);
  return {
    grid,
    winnings: lines.winnings,
    winningPositions: lines.winningPositions,
    winningLines: lines.winningLines,
    scatterPositions,
    scatterCount: scatterPositions.length,
    freeSpinsAwarded: maszynyFreeSpinsFor(scatterPositions.length),
  };
}

export function spinMaszyny(bet: number, rng: () => number): MaszynySpinResult {
  return evaluateMaszynySpin(generateMaszynyGrid(rng), bet);
}
